import React, { useState } from "react";
import { Box, Grid, Stack, Typography, Button } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import colors from "../../../../../../utils/colors";

import { WafAction, SmartParseSensitivity, ChallengeKind } from "../../../../../../utils/constants";
import ConfigGlobalWAFSettingsModal from "./modal/ConfigGlobalWAFSettings";
import ConfigSDWAFSettingsModal from "./modal/ConfigSDWAFSettings";
import ConfigSmartParseWAFSettingsModal from "./modal/ConfigSmartParseWAFSettings";
import useWAFConfig from "../../../../../../hooks/user/useWAFConfig";
import useAuth from "../../../../../../hooks/useAuth";
import { ReactComponent as GlobalIcon } from "../../../../../../vendor/_new/icon/services/global.svg";
import { ReactComponent as SignatureIcon } from "../../../../../../vendor/_new/icon/sites/signature_waf.svg";
import FormHelpTooltip from "../../../../../common/FormHelpTooltip";

export const getWafAction = (value) => {
  switch (value) {
    case WafAction.DETECT:
      return "Detect";
    case WafAction.BLOCK:
      return "Block";
    case WafAction.CHALLENGE:
      return "Challenge";
    case WafAction.ALL:
      return "All";
    default:
      return "Unknown";
  }
};

export const getSmartParseSensitivity = (value) => {
  switch (value) {
    case SmartParseSensitivity.HIGH:
      return "High";
    case SmartParseSensitivity.MEDIUM:
      return "Medium";
    case SmartParseSensitivity.LOW:
      return "Low";
    default:
      return "Unknown";
  }
};

export const getSignatureWafLevel = (level) => {
  switch (level) {
    case 1:
      return "1 - Conservative";
    case 2:
      return "2 - Balanced";
    case 3:
      return "3 - Aggressive";
    case 4:
      return "4 - Paranoid";
    default:
      return "Unknown";
  }
};

export const getChallengeKindLabel = (kind) => {
  if (kind == null || kind === "") return "Default (Managed)";
  switch (kind) {
    case ChallengeKind.INVISIBLE:
      return "Invisible";
    case ChallengeKind.MANAGED:
      return "Managed";
    case ChallengeKind.ADAPTIVE:
      return "Adaptive";
    case ChallengeKind.INTERACTIVE:
      return "Interactive";
    default:
      return String(kind);
  }
};

const StatusBadge = ({ value, isBoolean = false }) => {
  if (isBoolean) {
    const isEnabled = !!value;
    const backgroundColor = isEnabled ? colors.green[10] : colors.red[10];
    const borderColor = isEnabled ? colors.green[100] : colors.red[100];
    const fontColor = isEnabled ? colors.green[300] : colors.red[100];
    return (
      <Stack
        alignItems={"center"}
        justifyContent={"center"}
        sx={{ minWidth: 80, px: 2, height: 28, bgcolor: backgroundColor, borderRadius: "20px", border: `1px solid ${borderColor}` }}
      >
        <Typography sx={ { fontSize: 13, lineHeight: "20px", color: fontColor }}>
          {isEnabled ? "Enabled" : "Disabled"}
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack
      alignItems={"center"}
      justifyContent={"center"}
      sx={{
        minWidth: 80,
        px: 2,
        height: 28,
        bgcolor: colors.neutral[100],
        borderRadius: "20px",
      }}
    >
      <Typography sx={ { fontSize: 13, lineHeight: "20px", color: colors.neutral[700] }}>
        {value}
      </Typography>
    </Stack>
  );
};

const SettingRow = ({ label, value, isBoolean = false }) => {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.5 }}>
      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
        {label}
      </Typography>
      <StatusBadge value={value} isBoolean={isBoolean} />
    </Stack>
  );
};

const GlobalSettingItem = ({ label, value, isBoolean = false }) => {
  return (
    <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", p: 3, flex: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
          {label}
        </Typography>
        <StatusBadge value={value} isBoolean={isBoolean} />
      </Stack>
    </Box>
  );
};

const WAFCard = ({ title, icon: Icon, onEdit, badge, infoTitle, infoText, children, isPrimary = false, canEdit = true }) => {
  return (
    <Box
      sx={{
        backgroundColor: isPrimary ? "#F8F9FF" : "#fff",
        borderRadius: "8px",
        p: 3,
        height: "100%",
        border: `1px solid ${isPrimary ? colors.primary[300] : colors.neutral[200]}`,
        boxShadow: isPrimary ? "0 8px 24px rgba(99, 113, 247, 0.18)" : "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {Icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: colors.primary[300],
                backgroundColor: isPrimary ? "rgba(99, 113, 247, 0.12)" : "transparent",
              }}
            >
              <Icon style={{ width: 24, height: 24 }} />
            </Box>
          )}
          <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
            {title}
          </Typography>
          {badge && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: colors.neutral[100],
                borderRadius: "4px",
                border: `1px solid ${colors.neutral[300]}`,
              }}
            >
              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.neutral[600] }}>
                {badge}
              </Typography>
            </Box>
          )}
          {isPrimary && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: "rgba(99, 113, 247, 0.12)",
                borderRadius: "4px",
                border: `1px solid ${colors.primary[300]}`,
              }}
            >
              <Typography sx={ { fontSize: 11, fontWeight: "medium", color: colors.primary[300] }}>
                Primary
              </Typography>
            </Box>
          )}
          {canEdit && (
            <Button
              onClick={onEdit}
              sx={{
                height: 24,
                backgroundColor: "transparent",
                fontSize: 12,
                lineHeight: "18px",
                color: colors.primary[300],
                textTransform: "none",
                fontWeight: "regular",
                px: 1,
                minWidth: "auto",
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              Edit
            </Button>
          )}
        </Stack>
      </Stack>
      <Box sx={{ flex: 1 }}>{children}</Box>
      {infoText && (
        <Box 
          sx={{ 
            mt: 2, 
            p: 2.5, 
            bgcolor: isPrimary ? "rgba(99, 113, 247, 0.12)" : colors.primary["alpha"],
            borderRadius: "12px",
            border: isPrimary ? `1px solid ${colors.primary[100]}` : "none",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ color: colors.primary[300], fontSize: 20, mt: 0.25 }} />
            <Box>
              <Typography sx={ { fontSize: 14, fontWeight: "bold", lineHeight: "21px", color: colors.primary[300], mb: 0.5 }}>
                {infoTitle || `About ${title}`}
              </Typography>
              <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                {infoText}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const ConfigGeneralTabContent = ({ siteUid, canConfigureWAFForSite }) => {
  const { isAuthenticated, homeController, wafdashController, websiteController, wafeventController, planController } = useAuth();
  const { wafConfig, getWAFConfig } = useWAFConfig();

  const [openGlobalConfigWafSettings, setGlobalConfigWafSettings] = useState(false);
  const handleGlobalConfigWafSettingsOpen = () => setGlobalConfigWafSettings(true);
  const handleGlobalConfigWafSettingsClose = () => setGlobalConfigWafSettings(false);

  const [openSDWafSettings, setSDWafSettings] = useState(false);
  const handleSDWafSettingsOpen = () => setSDWafSettings(true);
  const handleSDWafSettingsClose = () => setSDWafSettings(false);

  const [openSmartParseWafSettings, setSmartParseWafSettings] = useState(false);
  const handleSmartParseWafSettingsOpen = () => setSmartParseWafSettings(true);
  const handleSmartParseWafSettingsClose = () => setSmartParseWafSettings(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (websiteController) websiteController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();
      if (siteUid) {
        getWAFConfig(siteUid, true);
      }
    }
  }, [isAuthenticated, siteUid]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Stack spacing={3}>
        <Box sx={{ backgroundColor: colors.primary["alpha"], borderRadius: "8px", p: 3, border: `1px solid ${colors.primary[100]}` }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
            <Box sx={{ display: "flex", alignItems: "center", color: colors.primary[300] }}>
              <GlobalIcon style={{ width: 24, height: 24 }} />
            </Box>
            <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
              Global WAF settings
            </Typography>
            <FormHelpTooltip tooltip="Site-wide switches applied before per-engine settings: master WAF on/off, request body logging, and response caching. Smart Parse and Signature WAF engines are configured separately below." />
            {canConfigureWAFForSite && (
              <Button
                onClick={handleGlobalConfigWafSettingsOpen}
                sx={{
                  height: 24,
                  backgroundColor: "transparent",
                  fontSize: 12,
                  lineHeight: "18px",
                  color: colors.primary[300],
                  textTransform: "none",
                  fontWeight: "regular",
                  px: 1,
                  minWidth: "auto",
                  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                }}
              >
                Edit
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing={2}>
            <GlobalSettingItem label="Global WAF" value={wafConfig?.active} isBoolean={true} />
            <GlobalSettingItem label="Payload Logging" value={wafConfig?.audit_req_body} isBoolean={true} />
            <GlobalSettingItem label="Use cache" value={wafConfig?.use_cache} isBoolean={true} />
          </Stack>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <WAFCard
              title="Layerd AI Smart Parse"
              icon={GlobalIcon}
              onEdit={handleSmartParseWafSettingsOpen}
              infoText="Layerd AI Smart Parse uses advanced AI reasoning with intelligent request parsing and validation to detect SQL injection, XSS, command injection, and other attacks with configurable sensitivity levels."
              isPrimary={true}
              canEdit={canConfigureWAFForSite}
            >
              <SettingRow label="Layerd AI Smart Parse" value={wafConfig?.smart_parse_module_active} isBoolean={true} />
              <SettingRow label="Smart Parse Sensitivity" value={getSmartParseSensitivity(wafConfig?.smart_parse_sensitivity)} isBoolean={false} />
              <SettingRow label="WAF Action" value={getWafAction(wafConfig?.waf_action_smart_parse)} isBoolean={false} />
            </WAFCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <WAFCard 
              title="Layerd Signature WAF" 
              icon={SignatureIcon} 
              onEdit={handleSDWafSettingsOpen}
              infoText="Layerd Signature WAF uses proprietary threat signatures developed by Layerd AI to detect and block known attack patterns with high accuracy and minimal false positives."
              canEdit={canConfigureWAFForSite}
            >
              <SettingRow label="Layerd Signature WAF" value={wafConfig?.sd_sig_module_active} isBoolean={true} />
              <SettingRow label="WAF Action" value={getWafAction(wafConfig?.waf_action_sd_sig)} isBoolean={false} />
              <SettingRow
                label="WAF Sensitivity"
                value={getSignatureWafLevel(wafConfig?.signature_waf_level ?? 1)}
                isBoolean={false}
              />
              {wafConfig?.waf_action_sd_sig === WafAction.CHALLENGE && (
                <SettingRow
                  label="Challenge kind"
                  value={getChallengeKindLabel(wafConfig?.waf_challenge_kind_sd_sig)}
                  isBoolean={false}
                />
              )}
            </WAFCard>
          </Grid>

        </Grid>
      </Stack>

      <ConfigGlobalWAFSettingsModal siteUid={siteUid} open={openGlobalConfigWafSettings} handleClose={handleGlobalConfigWafSettingsClose} />
      <ConfigSDWAFSettingsModal siteUid={siteUid} open={openSDWafSettings} handleClose={handleSDWafSettingsClose} />
      <ConfigSmartParseWAFSettingsModal siteUid={siteUid} open={openSmartParseWafSettings} handleClose={handleSmartParseWafSettingsClose} />
    </>
  );
};
export default ConfigGeneralTabContent;
